import React from "react";
import { useTheme } from "../../ThemeContext";

const ThemedChip = ({
  children,
  variant = "default",
  size = "medium",
  customStyle = {},
  className = "",
  onClick = null,
  ...props
}) => {
  const { themeMode } = useTheme();

  // Predefined variants with theme-aware colors
  const variants = {
    default: {
      backgroundColor: themeMode === "dark" ? "#151138" : "#eeeeee",
      color: themeMode === "dark" ? "#ffff" : "#000000",
      border: themeMode === "dark" ? "0.0625rem solid #252144" : "0.0625rem solid #bcbcbc",
    },
    primary: {
      backgroundColor: themeMode === "dark" ? "#1e3a8a" : "#e3f2fd",
      color: themeMode === "dark" ? "#93c5fd" : "#1976d2",
      border: themeMode === "dark" ? "0.0625rem solid #3b82f6" : "0.0625rem solid #bbdefb",
    },
    secondary: {
      backgroundColor: themeMode === "dark" ? "#7c2d12" : "#f3e5f5",
      color: themeMode === "dark" ? "#fca5a5" : "#7b1fa2",
      border: themeMode === "dark" ? "0.0625rem solid #dc2626" : "0.0625rem solid #ce93d8",
    },
    success: {
      backgroundColor: themeMode === "dark" ? "#dcfce7" : "#e8f5e8",
      color: themeMode === "dark" ? "#15803d" : "#2e7d32",
      border: themeMode === "dark" ? "0.0625rem solid #22c55e" : "0.0625rem solid #81c784",
    },
    warning: {
      backgroundColor: themeMode === "dark" ? "#fef3c7" : "#fff3e0",
      color: themeMode === "dark" ? "#92400e" : "#f57c00",
      border: themeMode === "dark" ? "0.0625rem solid #f59e0b" : "0.0625rem solid #ffb74d",
    },
    error: {
      backgroundColor: themeMode === "dark" ? "#fecaca" : "#ffebee",
      color: themeMode === "dark" ? "#991b1b" : "#d32f2f",
      border: themeMode === "dark" ? "0.0625rem solid #ef4444" : "0.0625rem solid #e57373",
    },
    info: {
      backgroundColor: themeMode === "dark" ? "#164e63" : "#e1f5fe",
      color: themeMode === "dark" ? "#67e8f9" : "#0277bd",
      border: themeMode === "dark" ? "0.0625rem solid #0891b2" : "0.0625rem solid #4fc3f7",
    },
  };

  // Size variants
  const sizes = {
    small: {
      padding: "0.25rem 0.5rem",
      fontSize: "0.75rem",
      borderRadius: "0.375rem",
    },
    medium: {
      padding: "0.375rem 0.75rem",
      fontSize: "0.875rem",
      borderRadius: "0.5rem",
    },
    large: {
      padding: "0.5rem 1rem",
      fontSize: "1rem",
      borderRadius: "0.625rem",
    },
  };

  // Combine styles
  const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "500",
    textTransform: "capitalize",
    lineHeight: "1.2",
    transition: "all 0.2s ease-in-out",
    cursor: onClick ? "pointer" : "default",
    userSelect: "none",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
    ...variants[variant],
    ...sizes[size],
    ...customStyle,
    // Hover effects for clickable chips
    ...(onClick && {
      ":hover": {
        opacity: 0.8,
        transform: "translateY(-0.0625rem)",
        boxShadow: themeMode === "dark" 
          ? "0 0.125rem 0.25rem rgba(0, 0, 0, 0.3)" 
          : "0 0.125rem 0.25rem rgba(0, 0, 0, 0.1)",
      },
    }),
  };

  return (
    <span
      className={`themed-chip ${className}`}
      style={chipStyle}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(e);
        }
      } : undefined}
      {...props}
    >
      {children}
    </span>
  );
};

export default ThemedChip; 